import { useState, useRef, useEffect } from "react";
import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  linkWithCredential,
  signInWithCredential,
  signOut,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { auth, db, functions } from "../../../firebase.js";
import {
  ONBOARDING_STATUS_NEEDS_PROFILE,
  ONBOARDING_STATUS_LEGACY_INCOMPLETE,
} from "../userOnboardingStatus.js";
import phoneStyles from "./PhoneAuth.module.css";

/** Firebase SMS codes are 6 digits */
const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;

/** `normal` = checkbox widget (easier to debug). `invisible` = bind to Continue / Resend buttons per Firebase guidance. */
function recaptchaSizeMode() {
  const raw = import.meta.env.VITE_FIREBASE_PHONE_RECAPTCHA_SIZE;
  return raw === "normal" ? "normal" : "invisible";
}

function authPhoneErrorMessage(code, fallback) {
  switch (code) {
    case "auth/invalid-phone-number":
      return "Enter a valid phone number.";
    case "auth/missing-phone-number":
      return "Enter a phone number.";
    case "auth/quota-exceeded":
      return "SMS quota exceeded. Try again later.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
      return "This phone number is already linked to another account. Log in with it instead.";
    case "auth/provider-already-linked":
      return "This phone number is already verified on your account.";
    case "auth/invalid-verification-code":
      return "Invalid code. Check the SMS and try again.";
    case "auth/code-expired":
      return "This code expired. Tap Send code again.";
    case "auth/session-expired":
      return "Session expired. Send a new code.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a bit and try again.";
    case "auth/captcha-check-failed":
      if (
        typeof fallback === "string" &&
        /hostname\s*match\s*not\s*found/i.test(fallback)
      ) {
        return "Firebase blocked this URL: add its hostname under Authentication → Settings → Authorized domains (hostname only, e.g. xyz.vercel.app), save, reload, then try SMS again.";
      }
      return "reCAPTCHA check failed. Refresh the page, disable strict blockers for this site, and try again.";
    case "auth/missing-client-identifier":
      return "Phone sign-in is misconfigured (reCAPTCHA / authorized domains). Check Firebase Console.";
    case "auth/billing-not-enabled":
      return "Phone SMS requires the Firebase project used by this app to be on the Blaze plan. If Console already shows Blaze: the deployed app may be pointing at a different Firebase project—copy all VITE_FIREBASE_* values from Project settings → Your apps (same project you upgraded), redeploy, then retry.";
    default:
      return fallback || "Something went wrong. Try again.";
  }
}

/** Firestore not provisioned / wrong API — distinct from Auth failures */
function verifyChainUserMessage(error) {
  const m = typeof error?.message === "string" ? error.message : "";
  if (
    m.includes("does not exist for project") ||
    m.includes("add a Cloud Datastore or Cloud Firestore") ||
    /database\s*\(default\)\s*does not exist/i.test(m)
  ) {
    return "Firestore isn’t set up for this Firebase project yet. In Firebase Console: Build → Firestore Database → Create database, pick a region, then deploy your rules if needed and try again.";
  }
  return authPhoneErrorMessage(error.code, error.message);
}

function toE164Us(digits) {
  if (digits.startsWith("1")) return `+${digits}`;
  return `+1${digits}`;
}

/**
 * Confirms phone exists in Firebase Auth (Admin SDK) — avoids SMS for unknown numbers on login.
 * Requires deployed `checkPhoneRegistered` + Blaze (billing) for SMS and typical Functions deploy.
 */
let checkPhoneRegisteredCallable;
function getCheckPhoneRegisteredCallable() {
  if (!checkPhoneRegisteredCallable) {
    checkPhoneRegisteredCallable = httpsCallable(
      functions,
      "checkPhoneRegistered",
    );
  }
  return checkPhoneRegisteredCallable;
}

async function syncUserDocAfterPhoneVerified() {
  const user = auth.currentUser;
  if (!user?.phoneNumber) {
    throw new Error("Phone verification incomplete.");
  }
  // Avoid force-refresh here: after signInWithCredential/linkWithCredential, getIdToken(true) has
  // been observed to hang on some localhost setups; Firestore already uses the current Auth session.

  const userDocRef = doc(db, "users", user.uid);
  const phone = user.phoneNumber;
  const hasGoogle = user.providerData.some(
    (p) => p.providerId === "google.com",
  );

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(userDocRef);
    if (!snap.exists()) {
      transaction.set(userDocRef, {
        phoneNumber: phone,
        phoneVerified: true,
        updatedAt: serverTimestamp(),
        lastSignInAt: serverTimestamp(),
        email: user.email ?? null,
        displayName: user.displayName ?? null,
        photoURL: user.photoURL ?? null,
        authProviders: {
          email: Boolean(user.email),
          google: hasGoogle,
          phone: true,
        },
        onboardingStatus: ONBOARDING_STATUS_NEEDS_PROFILE,
        createdAt: serverTimestamp(),
      });
    } else {
      const prev = snap.data();
      const patch = {
        phoneNumber: phone,
        phoneVerified: true,
        updatedAt: serverTimestamp(),
        lastSignInAt: serverTimestamp(),
        "authProviders.phone": true,
      };
      if (prev?.onboardingStatus === ONBOARDING_STATUS_LEGACY_INCOMPLETE) {
        patch.onboardingStatus = ONBOARDING_STATUS_NEEDS_PROFILE;
      }
      transaction.update(userDocRef, patch);
    }
  });
}

/**
 * Firebase phone verification (real SMS or Console test numbers).
 *
 * mode="signIn" — phone-only account (`signInWithCredential`)
 * mode="link"   — add phone to signed-in email/Google user (`linkWithCredential`)
 *
 * onStepChange — reports `"phone"` | `"verify"` for parent header titles.
 */
export default function PhoneAuth({
  onSuccess,
  onError,
  /**
   * Called with the verified credential when linking fails because the phone is
   * already on another account (guest trials hit this when the visitor's real
   * account uses that number). Return truthy after signing into that account and
   * verification continues instead of dead-ending.
   */
  onCredentialInUse,
  /**
   * Awaited immediately before `signInWithCredential`, which replaces the current
   * Firebase user. Guest trials use it to snapshot data that becomes unreadable
   * once the uid changes.
   */
  onBeforeSignIn,
  onStepChange,
  /** Reports phone-field focus so the parent can compact the flow chrome. */
  onPhoneFieldFocusChange,
  mode = "signIn",
  /** `login` — callable checks Auth before SMS; backup: reject new users after OTP (`isNewUser`). */
  phoneEntry = "register",
}) {
  const [step, setStep] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneInputFocused, setPhoneInputFocused] = useState(false);
  const [codeInputFocused, setCodeInputFocused] = useState(false);
  const [codeDigits, setCodeDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [smsRequiredConsent, setSmsRequiredConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const codeRefs = useRef([]);
  const timerRef = useRef(null);
  /** Delays clearing focus layout so checkbox taps don't flash the full chrome. */
  const phoneFocusBlurTimerRef = useRef(null);
  /** Invisible reCAPTCHA should use a visible button as container (Firebase phone-auth docs). */
  const phoneSubmitBtnRef = useRef(null);
  const resendSmsBtnRef = useRef(null);
  /** Visible widget (`VITE_FIREBASE_PHONE_RECAPTCHA_SIZE=normal`) mounts here. */
  const recaptchaNormalHostRef = useRef(null);
  const verifierRef = useRef(null);
  const verificationIdRef = useRef(null);
  /** Set when a pre-check found the number on another account (see `requestSms`). */
  const signInInsteadOfLinkRef = useRef(false);

  useEffect(() => {
    onStepChange?.(step === "verify" ? "verify" : "phone");
  }, [step, onStepChange]);

  // Keep parent focus callback in a ref so its identity never re-fires this
  // effect (which was flashing phoneFieldFocused false→true and jittering layout).
  const onPhoneFieldFocusChangeRef = useRef(onPhoneFieldFocusChange);
  onPhoneFieldFocusChangeRef.current = onPhoneFieldFocusChange;

  useEffect(() => {
    const fieldFocused = phoneInputFocused || codeInputFocused;
    onPhoneFieldFocusChangeRef.current?.(fieldFocused);
    return () => onPhoneFieldFocusChangeRef.current?.(false);
  }, [phoneInputFocused, codeInputFocused]);

  /* Lock document scroll while a phone/code field is focused so iOS Safari
     cannot scroll the focused input (or Continue) out of the visible area. */
  useEffect(() => {
    const lockScroll =
      (phoneInputFocused && step === "phone") ||
      (codeInputFocused && step === "verify");
    if (!lockScroll) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;

    const pinScroll = () => {
      window.scrollTo(0, 0);
      html.scrollTop = 0;
      body.scrollTop = 0;
    };

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    pinScroll();

    const onScroll = () => {
      pinScroll();
    };

    window.addEventListener("scroll", onScroll, true);
    window.visualViewport?.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.visualViewport?.removeEventListener("scroll", onScroll);
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [phoneInputFocused, codeInputFocused, step]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (phoneFocusBlurTimerRef.current) {
        clearTimeout(phoneFocusBlurTimerRef.current);
      }
      try {
        verifierRef.current?.clear();
      } catch {
        /* ignore */
      }
      verifierRef.current = null;
    };
  }, []);

  const handlePhoneInputFocus = () => {
    if (phoneFocusBlurTimerRef.current) {
      clearTimeout(phoneFocusBlurTimerRef.current);
      phoneFocusBlurTimerRef.current = null;
    }
    setPhoneInputFocused(true);
  };

  const handlePhoneInputBlur = () => {
    if (phoneFocusBlurTimerRef.current) {
      clearTimeout(phoneFocusBlurTimerRef.current);
    }
    phoneFocusBlurTimerRef.current = setTimeout(() => {
      setPhoneInputFocused(false);
      phoneFocusBlurTimerRef.current = null;
    }, 400);
  };

  /** Keep the phone field focused (iOS keyboard stays up) when tapping consent. */
  const preservePhoneInputFocus = (e) => {
    if (e.target.closest?.("button")) return;
    e.preventDefault();
  };

  const toggleSmsRequiredConsent = (e) => {
    if (e.target.closest?.("button")) return;
    e.preventDefault();
    const next = !smsRequiredConsent;
    setSmsRequiredConsent(next);
    // Consent was the only gate for this alert — dismiss it once accepted.
    if (next) onError?.(null);
  };

  const toggleMarketingConsent = (e) => {
    e.preventDefault();
    setMarketingConsent((v) => !v);
  };
  /** Keep a code box focused (iOS keyboard stays up) when tapping Continue / resend. */
  const preserveCodeInputFocus = (e) => {
    e.preventDefault();
  };

  const clearVerifier = () => {
    try {
      verifierRef.current?.clear();
    } catch {
      /* ignore */
    }
    verifierRef.current = null;
  };

  const resolveRecaptchaContainer = () => {
    const size = recaptchaSizeMode();
    if (size === "normal") {
      return recaptchaNormalHostRef.current;
    }
    if (step === "verify") {
      return resendSmsBtnRef.current;
    }
    return phoneSubmitBtnRef.current;
  };

  const ensureVerifier = async () => {
    clearVerifier();
    const container = resolveRecaptchaContainer();
    if (!container) {
      throw new Error("reCAPTCHA anchor not ready. Try again.");
    }
    const size = recaptchaSizeMode();
    verifierRef.current = new RecaptchaVerifier(auth, container, {
      size,
      callback: () => {},
      "expired-callback": () => {
        clearVerifier();
      },
      "error-callback": () => {
        onError?.("reCAPTCHA error. Refresh the page and try again.");
      },
    });
    if (typeof verifierRef.current.render === "function") {
      try {
        await verifierRef.current.render();
      } catch (renderErr) {
        console.warn("[PhoneAuth] reCAPTCHA render:", renderErr);
      }
    }
    return verifierRef.current;
  };

  const startCountdown = () => {
    setCountdown(RESEND_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneNumberChange = (e) => {
    setPhoneNumber(formatPhoneNumber(e.target.value));
  };

  const requestSms = async () => {
    const digits = phoneNumber.replace(/\D/g, "");
    if (digits.length < 10) {
      onError?.("Please enter a phone number");
      return;
    }
    if (phoneEntry !== "login" && !smsRequiredConsent) {
      onError?.("Please accept SMS terms to continue");
      return;
    }

    // Drop any prior consent/phone alert before sending so it cannot linger on verify.
    onError?.(null);

    const e164 = toE164Us(digits);

    const skipLoginLookup =
      import.meta.env.VITE_SKIP_LOGIN_PHONE_REGISTERED_CHECK === "true";

    setLoading(true);
    verificationIdRef.current = null;
    signInInsteadOfLinkRef.current = false;
    try {
      if (phoneEntry === "login" && !skipLoginLookup) {
        try {
          const checkFn = getCheckPhoneRegisteredCallable();
          const { data } = await checkFn({ phoneE164: e164 });
          if (!data?.registered) {
            onError?.(
              "No account found with this phone number. Create an account if you're new, or try another way to log in.",
            );
            setLoading(false);
            return;
          }
        } catch (lookupErr) {
          console.error("checkPhoneRegistered:", lookupErr);
          const code = lookupErr.code || "";
          const msg =
            code === "functions/not-found"
              ? "Phone login check isn’t available. Deploy the checkPhoneRegistered Cloud Function (requires Blaze billing)."
              : code === "functions/internal"
                ? "Could not verify this phone number (check Cloud Function IAM / deployment)."
                : lookupErr.message ||
                  "Could not verify this phone number. Try again.";
          onError?.(msg);
          setLoading(false);
          return;
        }
      }

      // Linking a phone that already belongs to an account fails *and* burns the
      // verification session, so decide before the SMS goes out: sign into that
      // account instead and let the caller move any trial data across.
      if (mode === "link" && onCredentialInUse && !skipLoginLookup) {
        try {
          const checkFn = getCheckPhoneRegisteredCallable();
          const { data } = await checkFn({ phoneE164: e164 });
          signInInsteadOfLinkRef.current = Boolean(data?.registered);
        } catch (lookupErr) {
          // Without the check, attempt the link; a number that turns out to be
          // taken surfaces as "log in with it instead".
          console.error("checkPhoneRegistered (link pre-check):", lookupErr);
        }
      }

      const appVerifier = await ensureVerifier();
      const provider = new PhoneAuthProvider(auth);
      const verificationId = await provider.verifyPhoneNumber(
        e164,
        appVerifier,
      );
      verificationIdRef.current = verificationId;
      setStep("verify");
      startCountdown();
      setCodeDigits(Array(CODE_LENGTH).fill(""));
    } catch (error) {
      console.error("Phone SMS request error:", error);
      onError?.(authPhoneErrorMessage(error.code, error.message));
      clearVerifier();
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = (e) => {
    e?.preventDefault();
    requestSms();
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...codeDigits];
    next[index] = value;
    setCodeDigits(next);
    if (value && index < CODE_LENGTH - 1) {
      // preventScroll: stop iOS/Safari from scrolling the visualViewport on each
      // box hop — that rewrites --gg-viewport-* and jitters the fixed auth shell.
      codeRefs.current[index + 1]?.focus({ preventScroll: true });
    }
  };

  const handleCodeFocus = () => {
    setCodeInputFocused(true);
  };

  const handleCodeBlur = (e) => {
    // Sync path: relatedTarget is reliable on most browsers when hopping boxes.
    if (codeRefs.current.includes(e.relatedTarget)) return;
    // iOS often leaves relatedTarget null when hopping between code boxes;
    // check activeElement after the focus move settles.
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (codeRefs.current.includes(active)) return;
      setCodeInputFocused(false);
    });
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      codeRefs.current[index - 1]?.focus({ preventScroll: true });
    }
  };

  const handleCodePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    const next = [...codeDigits];
    for (let i = 0; i < CODE_LENGTH; i++) {
      next[i] = pasted[i] || "";
    }
    setCodeDigits(next);
    const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1);
    codeRefs.current[focusIdx]?.focus({ preventScroll: true });
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const code = codeDigits.join("");
    if (code.length !== CODE_LENGTH) return;

    const verificationId = verificationIdRef.current;
    if (!verificationId) {
      onError?.("Send a code first.");
      return;
    }

    setLoading(true);
    try {
      const digits = phoneNumber.replace(/\D/g, "");
      const e164 = toE164Us(digits);
      const credential = PhoneAuthProvider.credential(verificationId, code);

      if (
        mode === "link" &&
        auth.currentUser?.phoneNumber &&
        auth.currentUser.phoneNumber === e164
      ) {
        await syncUserDocAfterPhoneVerified();
        onSuccess?.();
        return;
      }

      if (signInInsteadOfLinkRef.current && onCredentialInUse) {
        // The number is on another account: the caller signs into it and brings
        // any guest data along; verification just has to complete here.
        const resolved = await onCredentialInUse(credential);
        if (!resolved) {
          throw new Error(
            "Could not sign in to the account for this phone number.",
          );
        }
      } else if (mode === "link" && auth.currentUser) {
        await linkWithCredential(auth.currentUser, credential);
      } else {
        await onBeforeSignIn?.();
        const userCred = await signInWithCredential(auth, credential);
        if (
          phoneEntry === "login" &&
          userCred.additionalUserInfo?.isNewUser === true
        ) {
          await signOut(auth);
          onError?.(
            "No account found with this phone number. Create an account if you're new, or try another way to log in.",
          );
          return;
        }
      }

      await syncUserDocAfterPhoneVerified();
      onSuccess?.();
    } catch (error) {
      console.error("Phone verify error:", error);
      onError?.(verifyChainUserMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fmtCountdown = (s) => {
    const m = String(Math.floor(s / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${m}:${sec}`;
  };

  const phoneDigits = phoneNumber.replace(/\D/g, "");
  const showPhoneContinue = phoneDigits.length >= 10;

  const codeFilled =
    codeDigits.join("").length === CODE_LENGTH && codeDigits.every(Boolean);

  const showNormalCaptcha = recaptchaSizeMode() === "normal";
  const isLoginPhoneEntry = phoneEntry === "login";
  const hideProgressForPhoneInput = step === "phone" && phoneInputFocused;
  const hideProgressForCodeInput = step === "verify" && codeInputFocused;

  return (
    <div
      className={[
        phoneStyles.phoneAuth,
        hideProgressForPhoneInput ? phoneStyles.phoneAuthKeyboardOpen : "",
        hideProgressForCodeInput ? phoneStyles.phoneAuthCodeKeyboardOpen : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showNormalCaptcha ? (
        <div
          ref={recaptchaNormalHostRef}
          className={phoneStyles.recaptchaNormalHost}
          aria-label="reCAPTCHA"
        />
      ) : null}

      {step === "verify" ? (
        <>
          <div
            className={`${phoneStyles.phoneIntro} ${phoneStyles.verifyIntro}`}
          >
            <h2 className={phoneStyles.phoneTitle}>Enter Code</h2>
            <p className={phoneStyles.phoneSubtitle}>
              We&apos;ve sent an SMS with an activation code to your phone
              {phoneNumber ? ` +1 ${phoneNumber}` : ""}
            </p>
          </div>

          <form
            onSubmit={handleVerifyCode}
            className={`${phoneStyles.verifyForm} ${hideProgressForCodeInput ? phoneStyles.verifyFormKeyboardOpen : ""}`}
          >
            <div className={phoneStyles.codeBoxes} onPaste={handleCodePaste}>
              {codeDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    codeRefs.current[i] = el;
                  }}
                  className={phoneStyles.codeBox}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  onFocus={handleCodeFocus}
                  onBlur={handleCodeBlur}
                  autoFocus={i === 0}
                  autoComplete={i === 0 ? "one-time-code" : undefined}
                />
              ))}
            </div>

            <div className={phoneStyles.verifyResendRow}>
              <button
                ref={resendSmsBtnRef}
                type="button"
                className={phoneStyles.verifyResendBtn}
                disabled={countdown > 0 || loading}
                onPointerDown={preserveCodeInputFocus}
                onMouseDown={preserveCodeInputFocus}
                onClick={() => requestSms()}
              >
                Send code again
              </button>
              {countdown > 0 && (
                <span className={phoneStyles.verifyResendTimer}>
                  {fmtCountdown(countdown)}
                </span>
              )}
            </div>

            <div className={phoneStyles.phoneContinueWrap}>
              <button
                type="submit"
                className={phoneStyles.phoneContinueBtn}
                disabled={loading || !codeFilled}
                onPointerDown={preserveCodeInputFocus}
                onMouseDown={preserveCodeInputFocus}
              >
                <span>{loading ? "Verifying\u2026" : "Continue"}</span>
                <i
                  className={`fas fa-arrow-right ${phoneStyles.phoneContinueIcon}`}
                  aria-hidden="true"
                />
              </button>
            </div>

            <div
              className={`${phoneStyles.progressFooter} ${hideProgressForCodeInput ? phoneStyles.progressFooterHidden : ""}`}
            >
              <div className={phoneStyles.progressLabels}>
                <span>1 / 2</span>
                <span>PHONE</span>
              </div>
              <div className={phoneStyles.progressBar} aria-hidden="true">
                <span className={phoneStyles.progressActive} />
                <span className={phoneStyles.progressInactive} />
              </div>
            </div>
          </form>
        </>
      ) : (
        <>
          <div
            className={`${phoneStyles.phoneIntro} ${isLoginPhoneEntry ? phoneStyles.phoneIntroLogin : ""}`}
            data-node-id={isLoginPhoneEntry ? "571:19817" : "571:16752"}
          >
            <h2 className={phoneStyles.phoneTitle}>
              {isLoginPhoneEntry
                ? "Enter your phone number"
                : "Add your mobile number"}
            </h2>
            <p className={phoneStyles.phoneSubtitle}>
              {isLoginPhoneEntry
                ? "We'll need to verify it's you to continue"
                : "Get text alerts when its time to place your purchase."}
            </p>
          </div>

          <form
            onSubmit={handleSendCode}
            className={`${phoneStyles.phoneForm} ${hideProgressForPhoneInput ? phoneStyles.phoneFormKeyboardOpen : ""}`}
          >
            <div
              className={`${phoneStyles.phoneFormFields} ${isLoginPhoneEntry ? phoneStyles.phoneFormFieldsLogin : ""}`}
            >
              <div className={phoneStyles.phoneFieldWrap}>
                <label
                  className={phoneStyles.phoneFieldLabel}
                  htmlFor="phone-number"
                >
                  Phone
                </label>
                <div
                  className={`${phoneStyles.phoneInputShell} ${isLoginPhoneEntry ? phoneStyles.phoneInputShellLogin : ""}`}
                >
                  <span className={phoneStyles.phonePrefix}>+1</span>
                  <span
                    className={phoneStyles.phoneDivider}
                    aria-hidden="true"
                  />
                  <input
                    id="phone-number"
                    className={phoneStyles.phoneInput}
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    onFocus={handlePhoneInputFocus}
                    onBlur={handlePhoneInputBlur}
                    placeholder="555-555-5555"
                    required
                    autoComplete="tel"
                    maxLength={12}
                  />
                </div>
              </div>

              <div className={phoneStyles.phoneContinueWrap}>
                <button
                  ref={phoneSubmitBtnRef}
                  type="submit"
                  className={phoneStyles.phoneContinueBtn}
                  disabled={loading || !showPhoneContinue}
                >
                  <span>{loading ? "Sending\u2026" : "Continue"}</span>
                  <i
                    className={`fas fa-arrow-right ${phoneStyles.phoneContinueIcon}`}
                    aria-hidden="true"
                  />
                </button>
              </div>

              {isLoginPhoneEntry ? (
                <div className={phoneStyles.loginAnotherWayWrap}>
                  <button
                    type="button"
                    className={phoneStyles.loginAnotherWay}
                    onClick={() => {}}
                  >
                    Login another way
                  </button>
                </div>
              ) : (
                <div className={phoneStyles.consentList}>
                  <label
                    className={phoneStyles.consentRow}
                    onPointerDown={preservePhoneInputFocus}
                    onMouseDown={preservePhoneInputFocus}
                    onClick={toggleSmsRequiredConsent}
                  >
                    <input
                      type="checkbox"
                      checked={smsRequiredConsent}
                      readOnly
                      tabIndex={-1}
                    />
                    <span className={phoneStyles.checkbox} aria-hidden="true" />
                    <span className={phoneStyles.consentText}>
                      Legal text about SMS being required.{" "}
                      <button type="button" className={phoneStyles.inlineLink}>
                        Privacy Policy.
                      </button>
                      *
                    </span>
                  </label>

                  <label
                    className={phoneStyles.consentRow}
                    onPointerDown={preservePhoneInputFocus}
                    onMouseDown={preservePhoneInputFocus}
                    onClick={toggleMarketingConsent}
                  >
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      readOnly
                      tabIndex={-1}
                    />
                    <span className={phoneStyles.checkbox} aria-hidden="true" />
                    <span className={phoneStyles.consentText}>
                      Optional about marketing messages.
                    </span>
                  </label>

                  <p className={phoneStyles.disclaimerText}>
                    *Legal disclaimer text about the think above.
                  </p>
                </div>
              )}
            </div>

            <div
              className={`${phoneStyles.progressFooter} ${hideProgressForPhoneInput ? phoneStyles.progressFooterHidden : ""}`}
            >
              <div className={phoneStyles.progressLabels}>
                <span>1 / 2</span>
                <span>PHONE</span>
              </div>
              <div className={phoneStyles.progressBar} aria-hidden="true">
                <span className={phoneStyles.progressActive} />
                <span className={phoneStyles.progressInactive} />
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
